import {
    Box,
    FormControl,
    FormHelperText,
    OutlinedInput,
    Typography,
} from '@mui/material'
import TruncatedText from './TruncatedText'
import SelectInput from './SelectInput'
import { ReactElement, useEffect, useState } from 'react'
import { useAppSelector } from '../../redux/hook'
import { AttributeType } from '../../constants/constants'
import { useTranslation } from 'react-i18next'

interface NumberInputAndUnitsProps {
    id: string
    label: string
    value: string
    onChange: any
    type: string
    dataType: string
    defaultUnits: any
    required?: boolean
    placeholder?: string
    disabled?: boolean
    errorMessage?: string
    range?: string
}

const NumberInputAndUnits = (props: NumberInputAndUnitsProps): ReactElement => {
    const {
        id,
        label,
        value,
        onChange,
        type = 'number',
        dataType,
        defaultUnits,
        required,
        placeholder,
        disabled,
        errorMessage = '',
        range = '',
    } = props
    const { t } = useTranslation()

    const unitsList = useAppSelector((state) => state.user.unitsList)
    const user = useAppSelector((state) => state.user.user)

    const [val, setVal] = useState<string>('')
    const [unit, setUnit] = useState<any>('')

    const [unitsOptions, setUnitsOptions] = useState<any>()

    useEffect(() => {
        let updatedUnits = []
        if (dataType === AttributeType.CURRENCY) {
            updatedUnits = [
                { id: 'USD', name: 'USD' },
                { id: 'EUR', name: 'EUR' },
            ]
        } else {
            updatedUnits =
                unitsList?.find((i) => i.id === dataType)?.units || []
            updatedUnits = updatedUnits?.map((i) => {
                return { id: i, name: i }
            })
        }
        setUnitsOptions(updatedUnits)
    }, [])

    useEffect(() => {
        if (value !== null && value !== undefined) {
            setVal(value)
        } else {
            setVal('')
        }
    }, [value])

    useEffect(() => {
        if (defaultUnits) {
            setUnit(defaultUnits)
        } else {
            const userUnits = user?.preferences?.units
            setUnit(userUnits ? userUnits[dataType] || '' : '')
        }
    }, [defaultUnits, unitsOptions])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const updatedVal = e.target.value
        onChange(id, updatedVal)
    }

    const onChangeUnits = (unit: string) => {
        onChange(id, unit, 'unit')
    }

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const { key, currentTarget } = e

        // Allow control keys like Backspace, Tab, and Arrow keys
        if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(key)) {
            return
        }

        const inputValue = currentTarget.value

        // Allow digits and a single dot for decimals
        if (/[\d.]/.test(key)) {
            // Prevent multiple dots
            if (key === '.' && inputValue.includes('.')) {
                e.preventDefault()
            }
        }
        // Allow a single '-' at the start for negative numbers
        else if (key === '-' && inputValue === '') {
            return
        }
        // Block all other inputs
        else {
            e.preventDefault()
        }
    }

    return (
        <Box sx={{ width: '100%' }}>
            <FormControl fullWidth>
                <Typography sx={{ opacity: '0.7', fontSize: '14px' }}>
                    {label}
                    {required ? '*' : ''}
                </Typography>
                <Box sx={{ display: 'flex' }}>
                    <Box sx={{ width: '100%' }}>
                        <OutlinedInput
                            size="small"
                            id={id}
                            label=""
                            type="text"
                            value={val}
                            onKeyDown={handleKeyPress}
                            onChange={handleChange}
                            sx={{
                                width: '100%',
                                '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
                                    {
                                        display: 'none',
                                    },
                                '& input[type=number]': {
                                    MozAppearance: 'textfield',
                                },
                                '& ::placeholder': {
                                    // opacity: 0.14,
                                },
                            }}
                            placeholder={
                                placeholder
                                    ? placeholder
                                    : t('enterLabel', { label })
                            }
                            inputProps={{
                                style: {
                                    fontSize: 14,
                                    opacity:
                                        val !== '' && val !== null
                                            ? '1'
                                            : '0.4',
                                },
                                type: `${type}`,
                                onKeyPress: (e) => {
                                    if (e.key === 'Enter') {
                                        // onEnter(
                                        //     id,
                                        //     (e.target as HTMLInputElement).value,
                                        // )

                                        // write your functionality here
                                        e.preventDefault()
                                    }
                                },
                            }}
                            disabled={!!disabled}
                            error={errorMessage !== ''}
                        />
                        {range?.trim() && (
                            <FormHelperText>
                                <TruncatedText
                                    sx={{ fontSize: '12px' }}
                                    text={`Accepted values (${range})`}
                                />
                            </FormHelperText>
                        )}
                        {errorMessage !== '' && (
                            <FormHelperText sx={{ color: 'red' }}>
                                {errorMessage}
                            </FormHelperText>
                        )}
                    </Box>
                    <Box sx={{ minWidth: '80px' }}>
                        <SelectInput
                            id={`${id}-units-selection`}
                            label=""
                            value={unit}
                            onChange={(id: string, val: string) =>
                                onChangeUnits(val)
                            }
                            list={unitsOptions}
                            hasEmpty={false}
                        />
                    </Box>
                </Box>
            </FormControl>
        </Box>
    )
}

export default NumberInputAndUnits
